// @ts-strict-ignore
import Divider from 'antd/lib/divider';
import Skeleton from 'antd/lib/skeleton';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import {
    AutomationType,
    MonitoringMetricHealthStatus,
    SubscriptionFeatureType,
    TrendStatus,
    UserRatingsMetricType,
} from '@/types';
import { GroupedMonitoringStatistic, MonitoringStatisticGroup } from '@/typescriptHelpers';
import { antGrey } from '../styles/globalStyles';
import { DescriptionPopoverIcon } from '../common/DescriptionPopoverIcon';
import { generateAutomationThunderboltIcon, lastUpdatedLabel, renamedMetricTooltip } from './monitoringHelpers';
import {
    colorForMonitoringMetricHealthStatus,
    displayThresholdForMonitoringStatisticGroup,
} from '@/helpers/displayHelpers';
import { nilTextForStatisticType, statTypeForMonitoringMetricType } from '@/helpers';
import { arrowForTrend } from '../orgOverview/OrgOverviewStatCards';
import { PlanRestrictedFeatureWrapper } from '@/components/planRestrictions/PlanRestrictedFeatureWrapper';
import { isDeltaMetric } from '@/components/appSettings/MonitoringMetricFormFields';
import IntegrationIcon from '../icons/IntegrationIcon';
import Flex from 'antd/lib/flex';
import { useSelector } from 'react-redux';
import { compact, uniq } from 'lodash';
import { RootState } from '@/reducers/reducers';
import { selectAppIntegrations } from '@/selectors/selectors';
import { getIntegrationLabel } from '@/helpers/integrationSelectHelpers';

export const relevantGroupsForMonitoringMetricCard = (
    groups: MonitoringStatisticGroup[],
): MonitoringStatisticGroup[] => {
    const deltaGroups = groups.filter((group) => isDeltaMetric(group.type));
    return deltaGroups.length > 0 ? deltaGroups : groups;
};

export const monitoringMetricCardsForStatistic = (
    statistic: GroupedMonitoringStatistic,
): GroupedMonitoringStatistic[] => {
    const groups = relevantGroupsForMonitoringMetricCard(statistic.groups);
    const groupCountBySeriesKey = new Map<string, number>();
    for (const group of groups) {
        groupCountBySeriesKey.set(group.seriesKey, (groupCountBySeriesKey.get(group.seriesKey) ?? 0) + 1);
    }

    if (!Array.from(groupCountBySeriesKey.values()).some((count) => count > 1)) {
        return [statistic];
    }

    return groups.map((group) => {
        const thresholdLabel = displayThresholdForMonitoringStatisticGroup(group);
        const groupCustomDisplayName = group.customDisplayName ?? statistic.customDisplayName;
        return {
            ...statistic,
            key: `${statistic.key}-${group.monitoringMetricId ?? `${group.seriesKey}-${thresholdLabel}`}`,
            title: groupCustomDisplayName ? statistic.title : `${statistic.title} (${thresholdLabel})`,
            customDisplayName: groupCustomDisplayName ? `${groupCustomDisplayName} (${thresholdLabel})` : undefined,
            description: group.description,
            groups: [group],
            mergedStatus: group.status,
        };
    });
};

export interface MonitoringMetricCardBaseProps extends GroupedMonitoringStatistic {
    appId: string;
    isLoading?: boolean;
    backgroundColor?: string;
    disableLoadShimmer?: boolean;
    description?: string;
    leadingIcon?: React.ReactNode;
}

export const MonitoringMetricCardBase: React.FC<MonitoringMetricCardBaseProps> = ({
    appId,
    mergedStatus,
    title,
    customDisplayName,
    description,
    groups,
    integrationType,
    isLoading,
    backgroundColor,
    disableLoadShimmer,
    leadingIcon,
}) => {
    const [isTitleTruncated, setIsTitleTruncated] = React.useState(false);

    const statusColor = colorForMonitoringMetricHealthStatus(mergedStatus);

    const isUsedForAutoAccelerate = groups.some((predicate) => {
        return predicate.automationUsage[AutomationType.AutoPromoteStablePhasedReleases].isUsed;
    });

    const isUsedForAutoHalt = groups.some((predicate) => {
        return predicate.automationUsage[AutomationType.AutoHaltUnstablePhasedReleases].isUsed;
    });

    const finalGroups = relevantGroupsForMonitoringMetricCard(groups);
    const metricDescription = finalGroups.length === 1 ? (finalGroups[0].description ?? description) : description;
    const activeCustomDisplayName =
        finalGroups.length === 1 ? (finalGroups[0].customDisplayName ?? customDisplayName) : customDisplayName;

    const isUserReviews = groups.some((group) =>
        Object.values(UserRatingsMetricType).includes(group.type as UserRatingsMetricType),
    );

    const integrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));
    const installationIds = uniq(compact(groups.map((group) => group.integrationInstallationId)));
    // Cards spanning several installations (composed or multi-destination) have no single integration to name.
    const integration =
        installationIds.length === 1 ? integrations.find((i) => i.installationId === installationIds[0]) : undefined;
    const integrationTooltip = integration && getIntegrationLabel(integration);

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) => (
                <div
                    style={{
                        backgroundColor: backgroundColor ?? antGrey.grey2,
                        border: `1px solid ${antGrey.grey4}`,
                        borderRadius: 8,
                        position: 'relative',
                        minHeight: 100,
                        minWidth: 232,
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: -1,
                            left: -1,
                            height: 'calc(100% + 2px)',
                            width: 7,
                            boxSizing: 'content-box',
                            borderRadius: '8px 0px 0px 8px',
                            backgroundColor: isDisabled ? antGrey.grey5 : statusColor,
                        }}
                    />

                    <Space
                        direction="vertical"
                        size={10}
                        style={{
                            paddingTop: 14,
                            paddingRight: 16,
                            paddingLeft: 22,
                            paddingBottom: 8,
                            width: '100%',
                        }}
                    >
                        {isLoading ?
                            <Skeleton
                                active={!disableLoadShimmer}
                                round={false}
                                paragraph={{
                                    rows: 2,
                                    style: {
                                        marginBottom: 0,
                                    },
                                }}
                            />
                        :   <>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        minHeight: 22,
                                    }}
                                >
                                    <Flex gap={8} style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                        {leadingIcon && <Flex align="center">{leadingIcon}</Flex>}
                                        {generateAutomationThunderboltIcon({
                                            isUsedForAutoAccelerate,
                                            isUsedForAutoHalt,
                                            prefix: 'This metric is being used for the',
                                        })}

                                        <PlanRestrictedFeatureWrapper
                                            feature={SubscriptionFeatureType.Rollouts}
                                            requiresTooltip
                                        >
                                            <Flex align="center" gap={8} style={{ minWidth: 0, maxWidth: '100%' }}>
                                                <Tooltip
                                                    title={renamedMetricTooltip(
                                                        activeCustomDisplayName,
                                                        title,
                                                        isTitleTruncated,
                                                    )}
                                                >
                                                    <Typography.Paragraph
                                                        ellipsis={{
                                                            rows: 1,
                                                            tooltip: activeCustomDisplayName ? undefined : title,
                                                            onEllipsis: setIsTitleTruncated,
                                                        }}
                                                        style={{
                                                            margin: 0,
                                                            minWidth: 0,
                                                            maxWidth: 232,
                                                            fontSize: 12,
                                                        }}
                                                    >
                                                        {activeCustomDisplayName || title}
                                                    </Typography.Paragraph>
                                                </Tooltip>
                                                {(metricDescription || isUserReviews) && (
                                                    <DescriptionPopoverIcon
                                                        description={
                                                            metricDescription ||
                                                            'This metric represents the average of the written customer reviews for the current version only.'
                                                        }
                                                    />
                                                )}
                                            </Flex>
                                        </PlanRestrictedFeatureWrapper>
                                    </Flex>
                                    <Tooltip title={integrationTooltip}>
                                        <span>
                                            <IntegrationIcon integrationId={integrationType} />
                                        </span>
                                    </Tooltip>
                                </div>

                                <div
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        alignContent: 'start',
                                    }}
                                >
                                    {finalGroups.map(
                                        (
                                            {
                                                type,
                                                value,
                                                unit,
                                                status,
                                                delta,
                                                change,
                                                previousVersion,
                                                previousValue,
                                                currentValueUpdatedAt,
                                            },
                                            index,
                                        ) => {
                                            const statType = statTypeForMonitoringMetricType(type);
                                            const trendStatus =
                                                !status ? TrendStatus.Neutral
                                                : status === MonitoringMetricHealthStatus.Healthy ? TrendStatus.Good
                                                : TrendStatus.Bad;
                                            const formattedValue =
                                                value !== undefined && value !== null ?
                                                    value
                                                :   <Typography.Text
                                                        type="secondary"
                                                        style={{
                                                            fontSize: 18,
                                                            fontWeight: 400,
                                                            whiteSpace: 'nowrap',
                                                            color: antGrey.grey6,
                                                            maxWidth: 170,
                                                            lineHeight: '30px',
                                                            marginBottom: 0,
                                                        }}
                                                        ellipsis={{
                                                            tooltip: nilTextForStatisticType(statType),
                                                        }}
                                                    >
                                                        {nilTextForStatisticType(statType)}
                                                    </Typography.Text>;

                                            const changeText =
                                                change === 0 ? 'No change'
                                                : change > 0 ? `Up ${delta}`
                                                : `Down ${delta}`;
                                            const versionText =
                                                previousVersion ? `version ${previousVersion}` : 'previous version';
                                            const tooltip = `${changeText} when compared to ${versionText} (${previousValue})`;
                                            const fontSize = finalGroups.length > 2 ? 18 : 20;
                                            const lastUpdated = lastUpdatedLabel(currentValueUpdatedAt);

                                            return (
                                                <React.Fragment key={index}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            maxWidth: `calc(${100 / finalGroups.length}% - ${
                                                                (finalGroups.length - 1) * 17
                                                            }px)`, // 17px is the width of the divider
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <Typography.Text
                                                            style={{
                                                                color: antGrey.grey6,
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            {unit}
                                                        </Typography.Text>

                                                        {!isDisabled ?
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    minWidth: 0,
                                                                    gap: 8,
                                                                }}
                                                            >
                                                                <Tooltip title={lastUpdated}>
                                                                    <Typography.Text
                                                                        ellipsis={{
                                                                            tooltip: !lastUpdated && value,
                                                                        }}
                                                                        style={{
                                                                            color: antGrey.grey8,
                                                                            fontSize: fontSize,
                                                                            width: '100%',
                                                                        }}
                                                                    >
                                                                        {value}
                                                                    </Typography.Text>
                                                                </Tooltip>
                                                                {delta && (
                                                                    <Tooltip title={tooltip}>
                                                                        <Space
                                                                            direction="horizontal"
                                                                            align="center"
                                                                            size={6}
                                                                        >
                                                                            {arrowForTrend(
                                                                                change,
                                                                                trendStatus,
                                                                                fontSize,
                                                                            )}
                                                                            {typeof formattedValue === 'string' ?
                                                                                <Typography.Text
                                                                                    style={{
                                                                                        color: antGrey.grey8,
                                                                                        fontSize: fontSize,
                                                                                        whiteSpace: 'nowrap',
                                                                                        maxWidth: 70,
                                                                                        marginBottom: 0,
                                                                                    }}
                                                                                >
                                                                                    {delta}
                                                                                </Typography.Text>
                                                                            :   formattedValue}
                                                                        </Space>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                        :   <Typography.Text
                                                                type="secondary"
                                                                style={{
                                                                    fontSize: 18,
                                                                    fontWeight: 400,
                                                                    whiteSpace: 'nowrap',
                                                                    color: antGrey.grey6,
                                                                    maxWidth: 170,
                                                                    lineHeight: '30px',
                                                                    marginBottom: 0,
                                                                }}
                                                            >
                                                                --
                                                            </Typography.Text>
                                                        }
                                                    </div>
                                                    {index < finalGroups.length - 1 && (
                                                        <Divider
                                                            type="vertical"
                                                            style={{ height: 40, marginInline: 10 }}
                                                        />
                                                    )}
                                                </React.Fragment>
                                            );
                                        },
                                    )}
                                </div>
                            </>
                        }
                    </Space>
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};

export const MonitoringMetricLoadingCard: React.FC<Partial<MonitoringMetricCardBaseProps>> = (props) => {
    return (
        <MonitoringMetricCardBase isLoading key={''} title={''} groups={[]} category={null} appId={null} {...props} />
    );
};

export const MonitoringMetricCard: React.FC<Omit<MonitoringMetricCardBaseProps, 'isLoading' | 'disableLoadShimmer'>> = (
    props,
) => {
    return <MonitoringMetricCardBase {...props} />;
};
